import React, { useEffect, useState } from "react";
import { Link, useMatch } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Icon,
  Stack,
  Switch,
  ListSubheader,
} from "investira.react.components";
import { BaseTemplate } from "../../components/templates";
import app from "../../utils/app";
import { PATHS } from "../../enums";
import { useEntidadesContext } from "../../contexts/EntidadesContext";
import usePrevious from "../../hooks/usePrevious";
import usePerfil from "../../hooks/usePerfil";

function Configuracoes() {
  let { pathnameBase } = useMatch("/configuracoes/*");
  const { state, actions } = useEntidadesContext();
  const { current, data } = useSelector((store) => store.entidade);

  const { usuarioPrioridade } = usePerfil();

  const { config } = data.find(
    (xEntidade) => xEntidade.entidade_id === current.default_entidade_id
  );

  const [custodiante, setCustodiante] = useState(
    config?.custodiante_conferencia || false
  );
  const [acesso, setAcesso] = useState(config?.acesso || false);
  const [assinatura, setAssinatura] = useState(config?.assinatura || false);

  const prevCustodiante = usePrevious(custodiante || false);
  const prevAcesso = usePrevious(acesso || false);
  const prevAssinatura = usePrevious(assinatura || false);

  function updateEntidadeAndRefresh(pConfig) {
    actions.handleModifyEntidade(
      {
        data: {
          config: pConfig,
        },
      },
      () => {
        actions.readEntidades();
        actions.readMembros();
      }
    );
  }

  useEffect(() => {
    if (prevCustodiante !== custodiante) {
      updateEntidadeAndRefresh({ custodiante_conferencia: custodiante });
    }
  }, [prevCustodiante, custodiante]);

  useEffect(() => {
    if (prevAcesso !== acesso) {
      updateEntidadeAndRefresh({ acesso: acesso });
    }
  }, [prevAcesso, acesso]);

  useEffect(() => {
    if (prevAssinatura !== assinatura) {
      updateEntidadeAndRefresh({ assinatura: assinatura });
    }
  }, [prevAssinatura, assinatura]);

  return (
    <BaseTemplate title="Configurações" maxWidth="md">
      <Stack spacing={2}>
        <List
          component={"nav"}
          subheader={<ListSubheader>Segurança</ListSubheader>}
        >
          <ListItem
            component={Link}
            to={pathnameBase + PATHS.configuracoes.nested["alterar-senha"].path}
          >
            <ListItemIcon>
              <Icon iconName={"key"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Alterar Senha"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
          </ListItem>
        </List>

        <List
          component={"nav"}
          subheader={<ListSubheader>Entidades</ListSubheader>}
        >
          <ListItem
            component={Link}
            to={pathnameBase + PATHS.configuracoes.nested.entidades.path}
          >
            <ListItemIcon>
              <Icon iconName={"parthenon"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Minhas Entidades"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
          </ListItem>
          <ListItem
            component={Link}
            to={
              pathnameBase +
              PATHS.configuracoes.nested.entidades.nested.membros.path
            }
          >
            <ListItemIcon>
              <Icon iconName={"users"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Membros"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
          </ListItem>

          <ListItem disabled={usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={"toll"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Controle de acesso"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
              secondary="Permissões e acessos"
            />
            <Switch
              edge="end"
              onChange={() => setAcesso((prevState) => !prevState)}
              checked={acesso}
              disabled={usuarioPrioridade > 2}
            />
          </ListItem>

          <ListItem disabled={usuarioPrioridade > 2 || !config?.acesso}>
            <ListItemIcon>
              <Icon iconName={"stamp"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Controle de Aprovação"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
            <Switch
              edge="end"
              onChange={() => setAssinatura((prevState) => !prevState)}
              checked={assinatura}
              disabled={usuarioPrioridade > 2 || !config?.acesso}
            />
          </ListItem>

          <ListItem
            component={usuarioPrioridade > 2 ? "span" : Link}
            to={
              pathnameBase +
              PATHS.configuracoes.nested.entidades.nested["alerta-usuarios"]
                .path
            }
            disabled={usuarioPrioridade > 2}
          >
            <ListItemIcon>
              <Icon iconName={"bell"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Controle de Notificações de Usuários"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
          </ListItem>
        </List>

        <List
          component={"nav"}
          subheader={<ListSubheader>Relatórios</ListSubheader>}
        >
          <ListItem
            component={usuarioPrioridade > 2 ? "span" : Link}
            to={pathnameBase + PATHS.configuracoes.nested["benchmarks"].path}
            disabled={usuarioPrioridade > 2}
          >
            <ListItemIcon>
              <Icon iconName={"chart_line"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Benchmarks"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
          </ListItem>

          <ListItem
            component={usuarioPrioridade > 2 ? "span" : Link}
            to={
              pathnameBase + PATHS.configuracoes.nested["fundos-fechados"].path
            }
            disabled={usuarioPrioridade > 2}
          >
            <ListItemIcon>
              <Icon iconName={"fund"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Fundos Excepcionalmente Não Abertos"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
          </ListItem>

          <ListItem disabled={usuarioPrioridade > 2}>
            <ListItemIcon>
              <Icon iconName={"policeman"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Controle de Conferência com Custodiante"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
            <Switch
              edge="end"
              onChange={() => setCustodiante((prevState) => !prevState)}
              checked={custodiante}
              disabled={usuarioPrioridade > 2}
            />
          </ListItem>
        </List>
        <List
          component={"nav"}
          subheader={<ListSubheader>Sessão</ListSubheader>}
        >
          <ListItem
            button
            onClick={() => {
              app.logout();
            }}
          >
            <ListItemIcon>
              <Icon iconName={"on_off"} size={21} color={"greenLight"} />
            </ListItemIcon>
            <ListItemText
              primary="Sair"
              primaryTypographyProps={{
                color: "textPrimary",
              }}
            />
          </ListItem>
        </List>
      </Stack>
    </BaseTemplate>
  );
}

Configuracoes.displayName = "Configuracoes";

export default Configuracoes;
